import { bytesPerRow, getPixel } from './bitmap.js';
import { HEAD_DOTS, MEDIA } from './media.js';
/**
 * Brother QL raster protocol (QL-800/810W/820NWB command reference), 300 dpi, no compression.
 * Hardware confirmation (AC-1.B4) is the final test; `mirror` exists so a left/right flip is a one-flag fix.
 */
const ROW_BYTES = HEAD_DOTS / 8; // 90
/** 200 bytes of 0x00 clear a half-received command. */
export const BROTHER_INVALIDATE = new Uint8Array(200);
/** ESC @ */
export const BROTHER_INITIALIZE = Uint8Array.of(0x1b, 0x40);
/** ESC i S: ask for the 32-byte status. */
export const BROTHER_STATUS_REQUEST = Uint8Array.of(0x1b, 0x69, 0x53);
/** Invalidate + initialise + status request, ready to send as one packet. */
export const BROTHER_STATUS_PACKET = concat([BROTHER_INVALIDATE, BROTHER_INITIALIZE, BROTHER_STATUS_REQUEST]);
function concat(parts) {
    const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
    let o = 0;
    for (const p of parts) {
        out.set(p, o);
        o += p.length;
    }
    return out;
}
/**
 * Builds the whole print job. `bitmap` is in printer orientation: width = across the head (<= printable width),
 * height = along the feed (raster lines). It is placed at the media's left offset; wider input is clipped.
 */
export function encodeBrotherQlRaster(bitmap, media, opts = {}) {
    const spec = MEDIA[media];
    const cut = opts.cut ?? true;
    const lines = bitmap.height;
    const pi = [
        0x80 | 0x02 | 0x04 | 0x08 | (opts.highQuality ? 0x40 : 0),
        spec.brother.mediaType,
        spec.brother.widthMm,
        spec.brother.lengthMm,
        lines & 0xff,
        (lines >> 8) & 0xff,
        (lines >> 16) & 0xff,
        (lines >>> 24) & 0xff,
        0x00, // first page
        0x00,
    ];
    const head = Uint8Array.of(0x1b, 0x69, 0x61, 0x01, // raster mode
    0x1b, 0x69, 0x7a, ...pi, // print information
    0x1b, 0x69, 0x4d, cut ? 0x40 : 0x00, // various mode: auto cut
    0x1b, 0x69, 0x41, 0x01, // cut every label
    0x1b, 0x69, 0x4b, cut ? 0x08 : 0x00, // expanded mode: cut at end
    0x1b, 0x69, 0x64, spec.continuous ? 35 : 0, 0x00, // feed margin
    0x4d, 0x00);
    const body = new Uint8Array(lines * (3 + ROW_BYTES));
    const usable = Math.min(bitmap.width, spec.printableDots.width);
    for (let y = 0; y < lines; y++) {
        const o = y * (3 + ROW_BYTES);
        body[o] = 0x67;
        body[o + 1] = 0x00;
        body[o + 2] = ROW_BYTES;
        const row = body.subarray(o + 3, o + 3 + ROW_BYTES);
        if (bitmap.width === usable && !opts.mirror && spec.offsetDots.left % 8 === 0 && bitmap.width % 8 === 0) {
            row.set(bitmap.data.subarray(y * bytesPerRow(bitmap.width), (y + 1) * bytesPerRow(bitmap.width)), spec.offsetDots.left / 8);
            continue;
        }
        for (let x = 0; x < usable; x++) {
            if (getPixel(bitmap, opts.mirror ? usable - 1 - x : x, y)) {
                const dot = spec.offsetDots.left + x;
                row[dot >> 3] = row[dot >> 3] | (0x80 >> (dot & 7));
            }
        }
    }
    return concat([BROTHER_INVALIDATE, BROTHER_INITIALIZE, head, body, Uint8Array.of(0x1a)]);
}
const MEDIA_BY_CODE = [
    { type: 0x0b, width: 17, length: 54, media: 'dk11204_17x54' },
    { type: 0x0b, width: 62, length: 29, media: 'dk11209_29x62' },
    { type: 0x0b, width: 62, length: 100, media: 'dk11202_62x100' },
    { type: 0x0a, width: 62, length: 0, media: 'dk22205_62x90' },
];
/**
 * Parses the 32-byte status reply. Error bits (byte 8 / byte 9): no media 0x01/8, end of media 0x02/8, cutter jam
 * 0x04/8, cover open 0x10/9, replace media 0x01/9, others -> `error`. `expected` makes a different loaded roll
 * report `media_mismatch`. `errorCode` is the two error bytes as hex ("0000" = none), null when no reply is usable.
 */
export function parseBrotherQlStatus(bytes, expected) {
    if (bytes.length < 32 || bytes[0] !== 0x80 || bytes[1] !== 0x20)
        return { state: 'unknown', media: null, errorCode: null };
    const e1 = bytes[8];
    const e2 = bytes[9];
    const errorCode = e1.toString(16).padStart(2, '0') + e2.toString(16).padStart(2, '0');
    const width = bytes[10];
    const type = bytes[11];
    const length = bytes[17];
    const loaded = MEDIA_BY_CODE.find((m) => (type & 0x0f) === (m.type & 0x0f) && m.width === width && (m.length === length || m.type === 0x0a))?.media ?? null;
    const statusType = bytes[18];
    let state;
    if (e1 & 0x03)
        state = 'no_media';
    else if (e2 & 0x10)
        state = 'cover_open';
    else if (e2 & 0x01)
        state = 'media_mismatch';
    else if (e1 & 0x04 || e1 & 0xc0 || e2 & 0xc4)
        state = 'error';
    else if (statusType === 0x02)
        state = 'error';
    else if (expected && loaded !== expected)
        state = 'media_mismatch';
    else if (statusType === 0x06 && bytes[19] === 0x01)
        state = 'busy';
    else if (e1 & 0x10)
        state = 'busy';
    else
        state = 'ready';
    return { state, media: loaded, errorCode };
}
//# sourceMappingURL=brother-ql.js.map